import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useRef, useState } from 'react'
import { DiscountTierPropsType, DiscountTierType } from '../../../api/appAPI'
import { EnqueueSnackbarType, showMessage } from '../../common/Helpers'
import DiscountTierUIBox from './DiscountTierUIBox'

interface IProps {
  tier: DiscountTierType

  editTier(
    id: number,
    tier: DiscountTierPropsType,
    enqueueSnackbar: EnqueueSnackbarType
  ): void
}

const EditDiscountTier: FC<IProps> = ({ tier, editTier, ...props }) => {
  const idRef = useRef(tier.id)
  const [tierProps, setTierProps] = useState({
    name: tier.name,
    extraDiscountStartPoint: tier.extraDiscountStartPoint,
    DPA_OfferCabinets: tier.DPA_OfferCabinets,
    PPFD_OfferCabinets: tier.PPFD_OfferCabinets,
    DPA_OfferSlatwallAndAcc: tier.DPA_OfferSlatwallAndAcc,
    PPFD_OfferSlatwallAndAcc: tier.PPFD_OfferSlatwallAndAcc,
    DPA_OfferOverheadStorage: tier.DPA_OfferOverheadStorage,
    PPFD_OfferOverheadStorage: tier.PPFD_OfferOverheadStorage,
  })
  const tierPropsRef = useRef(tierProps)

  const [needSave, setNeedSave] = useState(false)
  const needSaveRef = useRef(needSave)

  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    idRef.current = tier.id
    setTierProps({
      name: tier.name,
      extraDiscountStartPoint: tier.extraDiscountStartPoint,
      DPA_OfferCabinets: tier.DPA_OfferCabinets,
      PPFD_OfferCabinets: tier.PPFD_OfferCabinets,
      DPA_OfferSlatwallAndAcc: tier.DPA_OfferSlatwallAndAcc,
      PPFD_OfferSlatwallAndAcc: tier.PPFD_OfferSlatwallAndAcc,
      DPA_OfferOverheadStorage: tier.DPA_OfferOverheadStorage,
      PPFD_OfferOverheadStorage: tier.PPFD_OfferOverheadStorage,
    })

    setNeedSave(false)
  }, [tier])

  useEffect(() => {
    tierPropsRef.current = tierProps
    needSaveRef.current = needSave
  }, [needSave, tierProps])

  useEffect(
    () => () => {
      if (needSaveRef.current) {
        if (window?.confirm('Save discount tier?')) {
          handleSaveClick()
        }
      }
    },
    [tier]
  )

  const handleNameChange = (event: React.ChangeEvent<{ value: string }>) => {
    setTierProps({ ...tierProps, name: event.target.value.replace(/['"]/, '') })
    setNeedSave(true)
  }

  const handleExtraDiscountStartPointChange = (
    event: React.ChangeEvent<{ value: number }>
  ) => {
    setTierProps({ ...tierProps, extraDiscountStartPoint: event.target.value })
    setNeedSave(true)
  }

  const handleSaveClick = () => {
    const name = tierPropsRef.current.name
    const extraDiscountStartPoint = tierPropsRef.current.extraDiscountStartPoint
    const DPA_OfferCabinets = tierPropsRef.current.DPA_OfferCabinets
    const PPFD_OfferCabinets = tierPropsRef.current.PPFD_OfferCabinets
    const PPFD_OfferSlatwallAndAcc =
      tierPropsRef.current.PPFD_OfferSlatwallAndAcc
    const DPA_OfferSlatwallAndAcc = tierPropsRef.current.DPA_OfferSlatwallAndAcc
    const PPFD_OfferOverheadStorage =
      tierPropsRef.current.PPFD_OfferOverheadStorage
    const DPA_OfferOverheadStorage =
      tierPropsRef.current.DPA_OfferOverheadStorage

    if (name === '') {
      showMessage(enqueueSnackbar, 'Enter tier name')
      return
    }

    if (DPA_OfferCabinets.length !== PPFD_OfferCabinets.length) {
      showMessage(
        enqueueSnackbar,
        'The number of the values of DPA_OfferCabinets and PPFD_OfferCabinets must be equal'
      )
      return
    }

    if (DPA_OfferSlatwallAndAcc.length !== PPFD_OfferSlatwallAndAcc.length) {
      showMessage(
        enqueueSnackbar,
        'The number of the values of DPA_OfferSlatwallAndAcc and PPFD_OfferSlatwallAndAcc must be equal'
      )
      return
    }

    if (DPA_OfferOverheadStorage.length !== PPFD_OfferOverheadStorage.length) {
      showMessage(
        enqueueSnackbar,
        'The number of the values of DPA_OfferOverheadStorage and PPFD_OfferOverheadStorage must be equal'
      )
      return
    }

    needSaveRef.current = false
    setNeedSave(false)

    const newTierProps = {
      name,
      extraDiscountStartPoint,
      DPA_OfferCabinets,
      PPFD_OfferCabinets,
      PPFD_OfferSlatwallAndAcc,
      DPA_OfferSlatwallAndAcc,
      PPFD_OfferOverheadStorage,
      DPA_OfferOverheadStorage,
    } as DiscountTierType

    editTier(idRef.current, newTierProps, enqueueSnackbar)
  }

  return (
    <DiscountTierUIBox
      title="Edit Discount Tier"
      name={{
        value: tierProps.name,
        onChange: handleNameChange,
      }}
      extraDiscountStartPoint={{
        value: tierProps.extraDiscountStartPoint,
        onChange: handleExtraDiscountStartPointChange,
      }}
      DPA_OfferCabinets={{
        value: tierProps.DPA_OfferCabinets,
        onChange: (v) => {
          setTierProps({ ...tierProps, DPA_OfferCabinets: v })
          setNeedSave(true)
        },
      }}
      PPFD_OfferCabinets={{
        value: tierProps.PPFD_OfferCabinets,
        onChange: (v) => {
          setTierProps({ ...tierProps, PPFD_OfferCabinets: v })
          setNeedSave(true)
        },
      }}
      DPA_OfferSlatwallAndAcc={{
        value: tierProps.DPA_OfferSlatwallAndAcc,
        onChange: (v) => {
          setTierProps({ ...tierProps, DPA_OfferSlatwallAndAcc: v })
          setNeedSave(true)
        },
      }}
      PPFD_OfferSlatwallAndAcc={{
        value: tierProps.PPFD_OfferSlatwallAndAcc,
        onChange: (v) => {
          setTierProps({ ...tierProps, PPFD_OfferSlatwallAndAcc: v })
          setNeedSave(true)
        },
      }}
      DPA_OfferOverheadStorage={{
        value: tierProps.DPA_OfferOverheadStorage,
        onChange: (v) => {
          setTierProps({ ...tierProps, DPA_OfferOverheadStorage: v })
          setNeedSave(true)
        },
      }}
      PPFD_OfferOverheadStorage={{
        value: tierProps.PPFD_OfferOverheadStorage,
        onChange: (v) => {
          setTierProps({ ...tierProps, PPFD_OfferOverheadStorage: v })
          setNeedSave(true)
        },
      }}
      onButtonClick={handleSaveClick}

      // editable
    />
  )
}

export default EditDiscountTier
